import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { toast } from 'react-toastify';
import { useLanguage } from '../context/LanguageContext';
import './AdminDiscountManager.css';

const AdminDiscountManager = () => {
  const { t } = useLanguage();
  const [discounts, setDiscounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    walletAddress: '',
    discountPercent: '',
    reason: '',
    expiresAt: ''
  });

  const fetchDiscounts = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/smm/admin/discounts');
      setDiscounts(res.data?.discounts || []);
    } catch (e) {
      toast.error(t('failedToLoadDiscounts'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { fetchDiscounts(); }, [fetchDiscounts]);

  const resetForm = () => {
    setForm({ walletAddress: '', discountPercent: '', reason: '', expiresAt: '' });
    setEditingId(null);
    setShowForm(false);
  };

  const handleSave = async () => {
    try {
      if (editingId) {
        await api.put(`/smm/admin/discounts/${editingId}`, {
          discountPercent: Number(form.discountPercent),
          reason: form.reason,
          expiresAt: form.expiresAt || null
        });
        toast.success(t('discountUpdated'));
      } else {
        await api.post('/smm/admin/discounts', {
          walletAddress: form.walletAddress,
          discountPercent: Number(form.discountPercent),
          reason: form.reason,
          expiresAt: form.expiresAt || null
        });
        toast.success(t('discountCreated'));
      }
      resetForm();
      fetchDiscounts();
    } catch (e) {
      toast.error(e?.response?.data?.message || t('failedToSaveDiscount'));
    }
  };

  const handleEdit = (d) => {
    setForm({
      walletAddress: d.walletAddress,
      discountPercent: String(d.discountPercent),
      reason: d.reason || '',
      expiresAt: d.expiresAt ? d.expiresAt.slice(0, 16) : ''
    });
    setEditingId(d._id);
    setShowForm(true);
  };

  const handleToggle = async (d) => {
    try {
      await api.put(`/smm/admin/discounts/${d._id}`, { active: !d.active });
      fetchDiscounts();
    } catch (e) {
      toast.error(t('failedToUpdateDiscount'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('confirmDeleteDiscount'))) return;
    try {
      await api.delete(`/smm/admin/discounts/${id}`);
      toast.success(t('discountDeleted'));
      fetchDiscounts();
    } catch (e) {
      toast.error(t('failedToDeleteDiscount'));
    }
  };

  if (loading) return <div className="admin-discount-loading">{t('loading')}</div>;

  return (
    <div className="admin-discount-manager">
      <div className="admin-discount-header">
        <h2>{t('smmDiscounts')}</h2>
        <button className="adm-btn adm-btn-primary" onClick={() => { resetForm(); setShowForm(true); }}>
          + {t('addDiscount')}
        </button>
      </div>

      {showForm && (
        <div className="admin-discount-form">
          <h3>{editingId ? t('editDiscount') : t('addDiscount')}</h3>
          <div className="adm-form-row">
            <label>{t('walletAddress')}</label>
            <input
              type="text"
              value={form.walletAddress}
              onChange={e => setForm(f => ({ ...f, walletAddress: e.target.value }))}
              placeholder="0x..."
              disabled={!!editingId}
              className="adm-input"
            />
          </div>
          <div className="adm-form-row">
            <label>{t('discountPercent')}</label>
            <input
              type="number"
              min="1"
              max="100"
              value={form.discountPercent}
              onChange={e => setForm(f => ({ ...f, discountPercent: e.target.value }))}
              className="adm-input"
            />
          </div>
          <div className="adm-form-row">
            <label>{t('reason')}</label>
            <input
              type="text"
              value={form.reason}
              onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}
              placeholder={t('discountReasonPlaceholder')}
              className="adm-input"
            />
          </div>
          <div className="adm-form-row">
            <label>{t('expiresAt')}</label>
            <input
              type="datetime-local"
              value={form.expiresAt}
              onChange={e => setForm(f => ({ ...f, expiresAt: e.target.value }))}
              className="adm-input"
            />
          </div>
          <div className="adm-form-actions">
            <button className="adm-btn adm-btn-primary" onClick={handleSave}>{t('save')}</button>
            <button className="adm-btn adm-btn-secondary" onClick={resetForm}>{t('cancel')}</button>
          </div>
        </div>
      )}

      <div className="admin-discount-table-wrap">
        <table className="admin-discount-table">
          <thead>
            <tr>
              <th>{t('walletAddress')}</th>
              <th>{t('discountPercent')}</th>
              <th>{t('reason')}</th>
              <th>{t('expires')}</th>
              <th>{t('active')}</th>
              <th>{t('actions')}</th>
            </tr>
          </thead>
          <tbody>
            {discounts.length === 0 && (
              <tr><td colSpan="6" className="adm-empty">{t('noDiscounts')}</td></tr>
            )}
            {discounts.map(d => (
              <tr key={d._id} className={!d.active ? 'adm-row-inactive' : ''}>
                <td className="adm-mono">{d.walletAddress.slice(0, 6)}...{d.walletAddress.slice(-4)}</td>
                <td>{d.discountPercent}%</td>
                <td>{d.reason || '—'}</td>
                <td>{d.expiresAt ? new Date(d.expiresAt).toLocaleDateString() : t('permanent')}</td>
                <td>
                  <button
                    className={`adm-toggle ${d.active ? 'adm-toggle-on' : 'adm-toggle-off'}`}
                    onClick={() => handleToggle(d)}
                  >
                    {d.active ? t('yes') : t('no')}
                  </button>
                </td>
                <td>
                  <button className="adm-btn adm-btn-sm" onClick={() => handleEdit(d)}>{t('edit')}</button>
                  <button className="adm-btn adm-btn-sm adm-btn-danger" onClick={() => handleDelete(d._id)}>{t('delete')}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminDiscountManager;
